/**
 * Athos API request handler
 * Manages Athos API requests with session tracking and request header generation
 */

import { AthosParams } from 'libs/athos/classes/athos-params'
import { getApiOrigin } from 'libs/athos/athos-utils'

const REQUEST_TIMEOUT_MS = 8000
const RETRYABLE_STATUS = new Set<number>([408, 425, 429, 500, 502, 503, 504])

/**
 * Debug logging for raw Athos responses, opt-in via
 * `localStorage.setItem('athosDebug', 'true')` in the browser console.
 * Keeps every request/response pair inspectable without cluttering the
 * console for normal browsing.
 */
function logAthosResponse(source: string, url: URL, data: any): void {
  if (typeof window === 'undefined' || window.localStorage?.getItem('athosDebug') !== 'true') return
  console.groupCollapsed(`[Athos] ${source} ← ${url.pathname}`)
  console.log('URL:', url.toString())
  console.log('Response:', JSON.parse(JSON.stringify(data)))
  console.groupEnd()
}

export class AthosFetcher {
  params: AthosParams

  constructor(category?: string) {
    this.params = new AthosParams(category)
  }

  generateHeaders(): Headers {
    // Identity travels as query params (see updateParams) — custom athos-*
    // headers are not ingested by the API and would force a CORS preflight.
    return new Headers({
      Accept: 'application/json'
    })
  }

  generateRequestInit(): RequestInit {
    return {
      method: 'GET',
      headers: this.generateHeaders()
    }
  }

  generateOrigin(): string {
    return getApiOrigin(this.params.siteID)
  }

  updateParams(): void {
    // Required on every Athos API call: `beacon=true` (generates the
    // `responseId` Beacon 2.0 events reference) plus the first-party identity
    // cookie values as query params.
    this.params.set('beacon', 'true')
    this.params.setUserID()
    this.params.setSessionID()
    this.params.setPageLoadID()
    this.params.setSiteIDParam()
  }

  generateURL(endpoint = ''): URL {
    const origin = this.generateOrigin()
    const params = this.params.toString()
    return new URL(`${origin}${endpoint}?${params}`)
  }

  async fetch(controller?: AbortController): Promise<any> {
    if (!this.params.siteID) {
      // A blank `athos_*_site_id` theme setting renders as literal `null` via
      // Liquid's `nil | json` into `globalState.siteIds` — without this guard
      // that becomes a broken `https://null.a...` request instead of a no-op.
      console.warn('Athos request skipped: missing site ID', this.params)
      throw new Error('Athos request skipped: missing site ID')
    }

    const url = this.generateURL()
    const data = await this.request(url, controller?.signal, true)
    logAthosResponse(this.constructor.name, url, data)
    return data
  }

  /**
   * Perform a single request attempt with a timeout guard, retrying once on
   * transient failures. The caller's `externalSignal` (used to cancel a request
   * that a newer one supersedes) is respected without being mutated: each
   * attempt uses its own controller linked to the external signal, so a retry
   * after a timeout doesn't reuse an already-aborted caller controller.
   */
  async request(url: URL, externalSignal: AbortSignal | undefined, allowRetry: boolean): Promise<any> {
    const attemptController = new AbortController()
    const requestInit = this.generateRequestInit()
    requestInit.signal = attemptController.signal

    const onExternalAbort = () => attemptController.abort()
    if (externalSignal) {
      if (externalSignal.aborted) attemptController.abort()
      else externalSignal.addEventListener('abort', onExternalAbort, { once: true })
    }

    let timedOut = false
    const timeout = setTimeout(() => {
      timedOut = true
      attemptController.abort()
    }, REQUEST_TIMEOUT_MS)

    try {
      const response = await fetch(url, requestInit)

      if (!response.ok) {
        if (allowRetry && RETRYABLE_STATUS.has(response.status)) {
          return this.request(url, externalSignal, false)
        }
        throw new Error(`Athos request failed: ${response.status} ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      if (externalSignal?.aborted) throw error

      if (allowRetry) return this.request(url, externalSignal, false)

      if (timedOut) {
        throw new Error(`Athos request timed out after ${REQUEST_TIMEOUT_MS}ms`)
      }
      throw error
    } finally {
      clearTimeout(timeout)
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
    }
  }
}
